/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from "@ember/object";
import type RouterService from "@ember/routing/router-service";
import { service } from "@ember/service";
import dayjs from "dayjs";
import {
  validateDate,
  validateLength,
  validatePresence
} from "ember-changeset-validations/validators";
import type { BufferedChangeset } from "ember-changeset/types";
import type IntlService from "ember-intl/services/intl";
import saveSendMessage from "parog-web/components/chat/utils/save-send-message";
import FileUploadedEditionFormContentComponent from "parog-web/components/file-uploaded/edition-form/content";
import type { ForcBaseArgs } from "parog-web/components/forc/abstract/base-component";
import type {
  Payload as ForcPayload,
  ToastMessage
} from "parog-web/components/forc/abstract/journal-event-based-component";
import JournalEventBasedComponent from "parog-web/components/forc/abstract/journal-event-based-component";
import BioExamPrescribedJournalEvent from "parog-web/models/bio-exam-prescribed-journal-event";
import BioExamResultsReceivedJournalEvent from "parog-web/models/bio-exam-results-received-journal-event";
import ConsultationReportWrittenJournalEvent from "parog-web/models/consultation-report-written-journal-event";
import type DpCycleDeclaredJournalEvent from "parog-web/models/dp-cycle-declared-journal-event";
import FileUploadedJournalEvent from "parog-web/models/file-uploaded-journal-event";
import type MedicalEventJournalEvent from "parog-web/models/medical-event-journal-event";
import MedicalImagingPrescribedJournalEvent from "parog-web/models/medical-imaging-prescribed-journal-event";
import MedicationPrescribedJournalEvent from "parog-web/models/medication-prescribed-journal-event";
import ParamedicalCarePrescribedJournalEvent from "parog-web/models/paramedical-care-prescribed-journal-event";
import type Patient from "parog-web/models/patient";
import type AnalyticsService from "parog-web/services/analytics";
import type FetcherService from "parog-web/services/fetcher";
import type SemeiaSessionService from "parog-web/services/session";
import validateSometimes from "parog-web/validators/sometimes";

type JournalEventModelNameEnum =
  | "consultation-report-written-journal-event"
  | "bio-exam-results-received-journal-event"
  | "file-uploaded-journal-event"
  | "bio-exam-prescribed-journal-event"
  | "medication-prescribed-journal-event"
  | "medical-imaging-prescribed-journal-event"
  | "paramedical-care-prescribed-journal-event";

interface DocumentEditionFormComponentArgs extends ForcBaseArgs {
  patient: Patient;
}

interface Period {
  duration: number;
  unit: "day" | "days" | "week" | "weeks" | "month" | "months";
}

interface Model extends ForcPayload {
  willSendMessage: boolean;
  chatMessage: string;
}

/* eslint-disable parog-web/migrate-non-patch-forms */
export default class DocumentEditionFormComponent extends JournalEventBasedComponent<DocumentEditionFormComponentArgs> {
  @service declare analytics: AnalyticsService;
  @service declare fetcher: FetcherService;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare session: SemeiaSessionService;
  @service requestManager;

  static fullComponentId = "document/edition-form";

  journalEventProperties = ["occurredAt", "comments"];

  get validations(): Record<string, unknown> {
    return {
      documentType: validatePresence({
        presence: true,
        description: "Le type de document"
      }),
      occurredAt: [
        validatePresence({
          presence: true,
          description: "La date"
        }),
        validateDate({
          after: new Date("1900-01-01"),
          message: "La date ne peut pas être avant le 01/01/1900."
        }),
        validateDate({
          onOrBefore: dayjs().endOf("day").toDate(),
          message: "La date ne peut pas être dans le futur."
        })
      ],
      files: validatePresence({
        presence: true,
        description: "Le document"
      }),
      title: validateSometimes(
        [
          validatePresence({
            presence: true,
            description: "Le titre"
          }),
          validateLength({
            allowBlank: false,
            max: FileUploadedEditionFormContentComponent.titleMaximumChars,
            description: "Le titre"
          })
        ],
        function (this: BufferedChangeset) {
          /* eslint-disable-next-line */
          return this.get("documentType") === "other";
        }
      ),
      fileSubType: validateSometimes(
        [
          validatePresence({
            presence: true,
            description: "Le type de fichier"
          })
        ],
        function (this: BufferedChangeset) {
          return (
            /* eslint-disable-next-line */
            this.get("documentType") === "other" ||
            /* eslint-disable-next-line */
            this.get("documentType") === "prescr"
          );
        }
      ),
      consultationType: validateSometimes(
        [
          validatePresence({
            presence: true,
            description: "Le type de consultation"
          })
        ],
        function (this: BufferedChangeset) {
          /* eslint-disable-next-line */
          return this.get("documentType") === "cr";
        }
      )
    };
  }

  static forcDisplayName;

  get forcDisplayName(): string {
    return this.session.medic
      ? "Envoyer un document au patient"
      : "Envoyer un document à votre équipe de soin";
  }

  /**
   * This getter gives the mapping between
   * selected document type and related JE.
   */
  get items(): {
    [key: string]: {
      text: string;
      journalEventModelName: string | Array<string>;
    };
  } {
    return {
      bio: {
        text: "Résultats de biologie",
        journalEventModelName: "bio-exam-results-received-journal-event"
      },
      cr: {
        text: "Compte-rendu",
        journalEventModelName: "consultation-report-written-journal-event"
      },
      prescr: {
        text: "Ordonnances",
        journalEventModelName: [
          "bio-exam-prescribed-journal-event",
          "medication-prescribed-journal-event",
          "medical-imaging-prescribed-journal-event",
          "paramedical-care-prescribed-journal-event"
        ]
      },
      other: {
        text: "Autre",
        journalEventModelName: "file-uploaded-journal-event"
      }
    };
  }

  /**
   * The date label must change when bio exam
   * is selected.
   */
  get dateLabel(): string {
    return this.documentType === "bio"
      ? "Date de l'examen"
      : "Date du document";
  }

  /**
   * The document type from the changeset.
   */
  get documentType(): string {
    return this.changeset.get("documentType");
  }

  get validityOptionsMedication() {
    const periodArray: Period[] = [];

    periodArray.push({ duration: 1, unit: "day" });
    for (let i = 2; i <= 13; i++) {
      periodArray.push({
        duration: i,
        unit: "days"
      });
    }
    periodArray.push(
      { duration: 1, unit: "week" },
      { duration: 2, unit: "weeks" },
      { duration: 3, unit: "weeks" }
    );
    periodArray.push({ duration: 1, unit: "month" });
    for (let i = 1; i <= 6; i++) {
      periodArray.push({
        duration: i,
        unit: "months"
      });
    }
    return periodArray;
  }

  get validityOptionsBioExam() {
    return [
      "Un mois",
      "Deux mois",
      "Trois mois",
      "Quatre mois",
      "Cinq mois",
      "Six mois"
    ];
  }

  computePayload(): ForcPayload {
    const payload = super.computePayload();

    if (this.documentType === "other") {
      payload.title = this.changeset.get("title");
      payload.fileSubType = this.changeset.get("fileSubType");
      payload.body = this.changeset.get("body") || null;
    }

    if (this.documentType === "cr") {
      payload.consultationType = this.changeset.get("consultationType");
      payload.questions = this.changeset.get("questions") || null;
    }

    if (this.documentType === "bio") {
      payload.normalized = true;
    }

    if (
      this.documentType === "prescr" &&
      [
        "bio-exam-prescribed-journal-event",
        "medication-prescribed-journal-event"
      ].includes(this.changeset.get("fileSubType"))
    ) {
      payload.validityPeriod =
        this.changeset.get("fileSubType") ===
        "bio-exam-prescribed-journal-event"
          ? parseInt(this.changeset.get("validityPeriod"))
          : this.changeset.get("validityPeriod");
    }

    return payload;
  }

  get journalEventClassType(): any {
    return this.documentType === "prescr"
      ? (this.changeset.get("fileSubType") as JournalEventModelNameEnum)
      : (this.items[this.changeset.get("documentType")]
          .journalEventModelName as JournalEventModelNameEnum);
  }

  async save(): Promise<
    | BioExamResultsReceivedJournalEvent
    | ConsultationReportWrittenJournalEvent
    | FileUploadedJournalEvent
    | BioExamPrescribedJournalEvent
    | MedicationPrescribedJournalEvent
    | MedicalImagingPrescribedJournalEvent
    | ParamedicalCarePrescribedJournalEvent
    | MedicalEventJournalEvent
    | DpCycleDeclaredJournalEvent
    | undefined
  > {
    const journalEvent = await this.saveWithFiles();

    if (journalEvent) {
      if (!this.changeset.get("willSendMessage") && !this.session.patient)
        return journalEvent;

      let chatMessage;
      if (journalEvent instanceof ConsultationReportWrittenJournalEvent) {
        chatMessage = "Compte-rendu envoyé";
      } else if (journalEvent instanceof BioExamResultsReceivedJournalEvent) {
        chatMessage = "Résultats d'examen envoyés";
      } else if (journalEvent instanceof FileUploadedJournalEvent) {
        chatMessage = "Fichier envoyé";
      } else if (
        journalEvent instanceof BioExamPrescribedJournalEvent ||
        journalEvent instanceof MedicationPrescribedJournalEvent ||
        journalEvent instanceof MedicalImagingPrescribedJournalEvent ||
        journalEvent instanceof ParamedicalCarePrescribedJournalEvent
      ) {
        chatMessage = "Ordonnance envoyée";
      }

      await this.store
        .createRecord("patient-chat-message-sent-journal-event", {
          occurredAt: new Date(),
          triggeringJournalEvent: journalEvent,
          patient: journalEvent.patient,
          creator: journalEvent.creator,
          body: chatMessage
        })
        .save();

      await saveSendMessage(this.model as Model, {
        store: this.store,
        patient: this.patient,
        analytics: this.analytics
      });
    }

    await this.fetcher.query("patient-chat-message-sent-journal-event", {
      filter: {
        patient_id: this.args.patient.id
      },
      include: "creator"
    });

    return journalEvent;
  }

  get toastMessage(): ToastMessage | null {
    return this.session.patient
      ? {
          message: "Votre document a bien été envoyé.",
          title: "Document"
        }
      : super.toastMessage;
  }

  afterCompleted = (): void => {
    if (!this.session.medic) this.router.transitionTo("/patient-web/chat");
    else {
      const tab = this.changeset.get("willSendMessage")
        ? "consult-chat"
        : "consult-timeline";
      this.router.transitionTo(this.router.currentRouteName, {
        queryParams: { tab }
      });
    }
  };

  @action
  onDocumentTypeUpdate(documentType: string): void {
    const fileSubType = this.changeset.get("fileSubType");
    if (documentType == "other") {
      if (!fileSubType) {
        // Initialize fileSubType if "other" is selected
        this.changeset.set("fileSubType", "other");
      }
    }

    if (documentType != "other") {
      // Reinitialize fileSubType if "other" is not selected
      if (!fileSubType) return;

      this.changeset.set("fileSubType", undefined);
    }
  }
}
